/**
 * Create the store with dynamic reducers
 */
import { createStore, applyMiddleware, StoreEnhancer, Middleware } from 'redux';
import debounce from 'lodash/debounce';

import { AugmentedAction } from 'ia-react-core/~/interfaces/AugmentedAction';
import { ReducerInfo } from 'ia-react-core/~/interfaces/ReducerInfo';
import { AugmentedStore } from 'ia-react-core/~/interfaces/AugmentedStore';
import { CoreAction } from 'ia-react-core/~/interfaces/CoreAction';
import Schema from 'ia-react-core/~/schemas/Schema';

import { DeepPartial } from 'ia-react-core/~/interfaces/DeepPartial';
import compose from '../../../utils/components/_compose';
import REDUX from '../../../constants/REDUX';
import createRootReducer from './createRootReducer';
import { INIT_NESTED_REDUCERS, CLEAN_NESTED_REDUCERS } from './withStore.constants';

export interface ConfigureStoreOptions<State> {
  initialState?: DeepPartial<State>;
  reducers?: {
    [key in keyof State]: (state: State[key], action: any) => State[key];
  };
  extendReducer?: (reducer: (state: State, action: CoreAction) => State) => (state: State) => State;
  enhancers?: StoreEnhancer[];
  storeDecorators?: ((store: AugmentedStore<State>) => void)[] ;
  middlewares: Middleware<any, State, any>[];
  schema: Schema;
}

/**
  *
  * @ignore
  */
export default function configureStore<State extends object>(options: ConfigureStoreOptions<State>): AugmentedStore<State> {
  const {
    initialState = {} as ConfigureStoreOptions<State>['initialState'],
    middlewares = [] as ConfigureStoreOptions<State>['middlewares'],
    reducers = {} as ConfigureStoreOptions<State>['reducers'],
    storeDecorators = []as ConfigureStoreOptions<State>['storeDecorators'],
    enhancers = [] as ConfigureStoreOptions<State>['enhancers'],
    extendReducer = ((reducer) => reducer) as ConfigureStoreOptions<State>['extendReducer'],
  } = options;

  const allowClassActionMiddleware: Middleware = () => (next) => (action) => {
    if (typeof action !== 'function' && action.constructor !== Object) {
      return next({
        [REDUX.ACTION_NAMESPACE]: (action as AugmentedAction).constructor.namespace,
        [REDUX.ACTION_IDENTIFIER]: (action as AugmentedAction).constructor.identifier,
        ...(action as CoreAction),
        type: (action as CoreAction).type || (action as AugmentedAction).constructor.type,
      });
    }

    return next(action);
  };

  const combinedEnhancers = [
    ...enhancers,
    applyMiddleware(allowClassActionMiddleware, ...middlewares),
  ];

  // eslint-disable-next-line
   if (window.__REDUX_DEVTOOLS_EXTENSION__) {
    // eslint-disable-next-line
     combinedEnhancers.push(window.__REDUX_DEVTOOLS_EXTENSION__({
      // Has to be false to prevent bug where last 50 action are reapplied when replacing reducer
      shouldHotReload: false,
      trace: true,
    }));
  }

  let rootReducer: (state: State, action: CoreAction) => State = null;

  const rootReducers: ReducerInfo[] = Object.keys(reducers).reduce((result, key) => {
    result.push(({ key, reducer: reducers[key as keyof State] }));
    return result;
  }, []);

  interface StoreExtensions {
    injectedReducers: {
      unnamespacedReducers: ReducerInfo<any>[];
      uninitializedReducers: ReducerInfo<any>[];
      cleanedReducers: ReducerInfo<any>[];
      namespacedReducers: Record<string, {
        identified: Record<string, Record<string, ReducerInfo<any>[]>>;
        unidentified: ReducerInfo<any>[];
        all: ReducerInfo<any>[];
      }>;
    };
    currentChanges: {}[];
    selectorStore: Record<string, ((state: {}) => unknown)>;
    actionStore: Record<string, () => void>;
    middlewares: typeof middlewares;
    isReducerInjected: typeof isReducerInjected;
    injectReducer: typeof injectReducer;
    ejectReducer: typeof ejectReducer;
  }

  const storeExtensions: StoreExtensions = {
    injectedReducers: {
      unnamespacedReducers: [...rootReducers],
      namespacedReducers: {},
      uninitializedReducers: [],
      cleanedReducers: [],
    },
    middlewares,
    currentChanges: [],
    actionStore: {},
    selectorStore: {},
    isReducerInjected,
    injectReducer,
    ejectReducer,
  };

  const store = Object.assign(
    createStore(
      extendReducer(rootReducerWrapper),
      initialState as State,
      compose(...combinedEnhancers),
    ) as AugmentedStore<State>,
    // Extensions
    storeExtensions,
  );

  rootReducer = createRootReducer<State>(store, options.schema);

  storeDecorators.forEach((storeDecorator) => {
    storeDecorator(store);
  });

  const debouncedCleanReducers = debounce(() => {
    if (store.injectedReducers.cleanedReducers.length) {
      store.dispatch({ type: CLEAN_NESTED_REDUCERS });
    }
  }, 50);

  return store;

  function initializeReducers() {
    store.dispatch({ type: INIT_NESTED_REDUCERS });
  }

  function rootReducerWrapper(state: State, action: CoreAction) {
    if (rootReducer) {
      return rootReducer(state, action);
    }

    return state;
  }

  function injectReducer<ScopedState extends {} = {}>(key: string, reducerId: number, reducer: (state: ScopedState, action: CoreAction) => ScopedState, reducerOptions: Partial<ReducerInfo<ScopedState>>) {
    const { scope, namespace, initialState: currentInitialState, idName } = reducerOptions;
    const reducerInfo: ReducerInfo<ScopedState> = { key, reducerId, scope, reducer, initialState: currentInitialState, namespace, idName };

    const cancelledCleanupIndex = store.injectedReducers.cleanedReducers.findIndex((cleanedReducer) => (
      cleanedReducer.key === key &&
      cleanedReducer.namespace === namespace &&
      cleanedReducer.scope.join(',') === scope.join(',')
    ));

    if (cancelledCleanupIndex !== -1) {
      store.injectedReducers.cleanedReducers.splice(cancelledCleanupIndex, 1);
    }

    if (namespace) {
      injectNamespacedReducer(reducerInfo, reducerOptions);
    } else {
      injectUnamespacedReducer(reducerInfo);
    }

    store.injectedReducers.uninitializedReducers.push(reducerInfo);
    initializeReducers();

    return reducerInfo;
  }

  function injectNamespacedReducer<ScopedState>(reducerInfo: ReducerInfo<ScopedState>, { namespace, identifiers }: Partial<ReducerInfo<ScopedState>>) {
    const { namespacedReducers } = store.injectedReducers;

    namespacedReducers[namespace] = (
      namespacedReducers[namespace] ||
       { identified: {}, unidentified: [], all: [] }
    );

    if (identifiers) {
      Object.keys(identifiers).forEach((identifierName) => {
        const identifierValue = identifiers[identifierName];
        namespacedReducers[namespace].identified[identifierName] = namespacedReducers[namespace].identified[identifierName] || {};
        namespacedReducers[namespace].identified[identifierName][identifierValue] = namespacedReducers[namespace].identified[identifierName][identifierValue] || [];
        namespacedReducers[namespace].identified[identifierName][identifierValue].push(reducerInfo);
      });
    } else {
      namespacedReducers[namespace].unidentified.push(reducerInfo);
    }

    namespacedReducers[namespace].all.push(reducerInfo);
  }

  function injectUnamespacedReducer<ScopedState>(reducerInfo: ReducerInfo<ScopedState>) {
    const { unnamespacedReducers } = store.injectedReducers;
    unnamespacedReducers.push(reducerInfo);
  }

  function isReducerInjected<ScopedState>(
    key: string,
    reducerId: number,
    reducer: (state: ScopedState, action: CoreAction) => ScopedState,
    { scope, namespace }: Partial<ReducerInfo<ScopedState, CoreAction>>,
  ) {
    const { namespacedReducers, unnamespacedReducers } = store.injectedReducers;

    if (namespace && !namespacedReducers[namespace]) {
      return false;
    }

    const reducerStore = namespace ? namespacedReducers[namespace].all : unnamespacedReducers;

    return Boolean(
      reducerStore.some((reducerInfo) => (
        reducerInfo.key === key &&
         reducerInfo.reducerId === reducerId &&
         (
           (!scope && !reducerInfo.scope) ||
           reducerInfo.scope.join('.') === scope.join('.')
         )
      )),
    );
  }

  function ejectReducer<ScopedState>(key: string, reducerId: number, { scope, namespace, identifiers, cleanState }: Partial<ReducerInfo<ScopedState>>) {
    if (namespace) {
      const { namespacedReducers } = store.injectedReducers;
      const reducerStore = namespacedReducers[namespace].all;
      excludeReducerFromStore(reducerStore, key, reducerId, scope);

      if (identifiers) {
        Object.keys(identifiers).forEach((identifierName) => {
          const identifierValue = identifiers[identifierName];
          const identifiedStore = namespacedReducers[namespace].identified[identifierName][identifierValue];
          excludeReducerFromStore(identifiedStore, key, reducerId, scope);
        });
      } else {
        const unidentifiedStore = namespacedReducers[namespace].unidentified;
        excludeReducerFromStore(unidentifiedStore, key, reducerId, scope);
      }
    } else {
      const { unnamespacedReducers } = store.injectedReducers;
      excludeReducerFromStore(unnamespacedReducers, key, reducerId, scope);
    }

    if (cleanState) {
      store.injectedReducers.cleanedReducers.push({ key, reducerId, scope, namespace });
      debouncedCleanReducers();
    }
  }

  function excludeReducerFromStore(reducerStore: ReducerInfo<any>[], key: string, reducerId: number, scope: string[]) {
    const scopedReducerIndex = reducerStore.findIndex((currentReducer) => (
      currentReducer.key === key &&
       currentReducer.reducerId === reducerId &&
       (
         (!scope && !currentReducer.scope) ||
         currentReducer.scope.join('.') === scope.join('.')
       )
    ));

    if (scopedReducerIndex !== -1) {
      reducerStore.splice(scopedReducerIndex, 1);
    }
  }
}
